
import { FuseNavigationItem } from '@fuse/components/navigation';
import { hasModulePermission } from 'app/core/user/module-permission-snapshot';
import { environment } from 'environments/environment';

export const defaultNavigation: FuseNavigationItem[] = [
    {
        id: 'overview',
        title: 'Overview',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:view-columns',
        link: '/overview',
    },
    {
        id: 'datasources',
        title: 'Data Sources',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:square-3-stack-3d',
        link: '/data-sources',
        permissionKey: 'data_source',
    },
    {
        id: 'ai-agents',
        title: 'SAI Agents',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:cpu-chip',
        link: '/ai-agents',
        permissionKey: 'agents'
    },
    {
        id: 'tracker',
        title: 'Tracker',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:presentation-chart-bar',
        link: '/tracker',
        permissionKey: 'trackers'
    },
    {
        id: 'activities',
        title: 'Activities',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:clipboard-document-check',
        link: '/activities',
        permissionKey: 'activities'
    },
    {
        // No `permissionKey`: the page lists only what the signed-in actor already owns or was
        // shared, enforced by the backend, so it needs no module permission of its own.
        id: 'artifactory',
        title: 'Artifactory',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:archive-box',
        link: '/artifactory',
    },
    {
        id: 'dashboards',
        title: 'Dashboards',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:squares-2x2',
        link: '/dashboards',
        // Three places name this key and all three must agree, or the module half-works in a way
        // that reads as a missing feature: this `permissionKey`, the `ROUTE_PERMISSION_MAP` entry in
        // `core/auth/guards/permission.guard.ts`, and `'dashboards'` in `MODULE_PERMISSION_KEYS`
        // (`core/user/user.service.ts`). The last one is what normalises the payload's level string
        // into a boolean; without it the sidebar's `=== true` compare fails and the item stays
        // hidden from exactly the people who hold the module. The other two already existed; this is
        // the third, added now that the module has somewhere to go.
        //
        // The key is `disabled` for every existing tenant and deliberately not backfilled, so the
        // hidden case is the normal one here rather than the exception.
        permissionKey: 'dashboards',
    },
    {
        // Harvester price audits (workspace ADR 0026). Same three-place key contract as `dashboards`
        // above: this `permissionKey`, `ROUTE_PERMISSION_MAP['harvester']` and `MODULE_PERMISSION_KEYS`.
        id: 'harvester',
        title: 'Harvester',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:shopping-bag',
        link: '/harvester',
        permissionKey: 'harvester',
    },
    {
        id: 'reports',
        title: 'Reports',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:paper-airplane',
        link: '/reports/generated-reports',
        permissionKey: 'generated_report',
    },
    {
        id: 'chatForAgent',
        title: 'SAI Chat',
        type: 'collapsable',
        role: 'company',
        icon: 'heroicons_outline:sparkles',
        children: [
            ...(!environment.production
                ? [
                    {
                        id: 'saiChat',
                        title: 'SAI Master',
                        type: 'basic' as const,
                        link: '/ai-chat/sai-master',
                        permissionKey: 'sai_chat',
                    },
                ]
                : []),
            {
                id: 'chat_ai',
                title: 'SAI Chat',
                type: 'basic',
                link: '/ai-chat/agent-chat'
            },
        ]

    },
    {
        id: 'procedures',
        title: 'Procedures',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:cube-transparent',
        link: '/procedures',
        permissionKey: 'procedures'
    },

    {
        id: 'aiForms',
        title: 'Hyper Forms',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:rectangle-group',
        link: '/hyper-forms',
        permissionKey: 'hyper_forms'
    },
    {
        id: 'channels',
        title: 'Channels',
        type: 'basic',
        // Organization Admin only: visible to the company owner, hidden for member/staff.
        role: 'company',
        icon: 'heroicons_outline:signal',
        link: '/channels',
        // The backend now gates `/v1/channels/` and `/v1/contacts/` on this key, so without it the
        // item would show for an organization whose every call on the page answers 403 - the same
        // defect `automation` and `cognition` were given their keys to fix.
        permissionKey: 'channels',
        hidden: (): boolean => (localStorage.getItem('userRole') || '') !== 'company',
    },
    {
        id: 'automations',
        title: 'Automations',
        type: 'basic',
        // Organization Admin only: an automation is a standing grant of authority to act, so it is
        // scoped exactly like Channels. Keep this in step with the RoleGuard on the route.
        role: 'company',
        icon: 'heroicons_outline:bolt',
        link: '/automations',
        permissionKey: 'automation',
        // TWO conditions, and the second one is why this item is not like Channels.
        //
        // The role check is the same scope Channels has. What Channels does NOT have is a backend
        // feature gate: `plugins/permission` carries a prefix rule for `/automation/`, so every
        // request this page makes is refused for a tenant whose root permission has the module off.
        // With only the role check, an Organization Admin of such a tenant saw the item, clicked it,
        // and got a 403 on both of the page's opening calls. A door that is shown and does not open
        // is worse than no door.
        //
        // `hasModulePermission` returns false while permissions are still loading, which hides the
        // item for a moment after sign-in and then reveals it. That is deliberate and it is safe
        // because Fuse evaluates this predicate inline in its template, so it re-runs on every
        // change-detection pass rather than once at load. See `module-permission-snapshot.ts`.
        hidden: (): boolean =>
            (localStorage.getItem('userRole') || '') !== 'company' ||
            !hasModulePermission('automation'),
    },
    {
        id: 'crm',
        title: 'CRM',
        type: 'collapsable',
        // All three roles — Leads is the field rep's screen. CRM Settings is narrower (§4.4 hides it
        // from `staff`), which is enforced by a `RoleGuard` on that child route and by the `hidden`
        // predicate on the child item below.
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:briefcase',
        permissionKey: 'crm',
        // Same two conditions as Automations, for the same reason: `plugins/permission` carries a
        // prefix rule for `/crm/`, so without the module gate an admin of a tenant that does not
        // hold CRM would see the item, click it, and collect a 403 on the first call. The role check
        // alone is not enough. See `module-permission-snapshot.ts` for why the load-time flicker is
        // the safe direction.
        hidden: (): boolean => !hasModulePermission('crm'),
        children: [
            {
                id: 'crmLeads',
                title: 'Leads',
                type: 'basic',
                link: '/crm/leads',
            },
            {
                id: 'crmVisits',
                title: 'Visits',
                type: 'basic',
                link: '/crm/visits',
            },
            {
                // Invoices raised off won leads. A real browse endpoint (`/crm/invoices/list`)
                // exists, so unlike Accounts/Attribution this is a legitimate nav destination.
                id: 'crmInvoices',
                title: 'Invoices',
                type: 'basic',
                link: '/crm/invoices',
            },
            {
                // Synced ERP products (read-only) plus product catalogues. A real browse endpoint
                // (`/crm/products`) exists, so this is a legitimate nav destination.
                id: 'crmProducts',
                title: 'Products',
                type: 'basic',
                link: '/crm/products',
            },
            {
                id: 'crmCustomers',
                title: 'Customers',
                type: 'basic',
                link: '/crm/customers/contacts',
            },
            {
                // Accounts and Attribution deliberately never appear — Accounts has no browse
                // endpoint, and attribution results are a property of a lead, not a destination.
                id: 'crmSettings',
                title: 'CRM Settings',
                type: 'basic',
                link: '/crm/settings',
                // Hidden from `staff`, matching the `RoleGuard` on the route (§4.4).
                hidden: (): boolean => (localStorage.getItem('userRole') || '') === 'staff',
            },
        ],
    },
    // Data Quality deliberately has NO nav item of its own. It is a sub-unit of Data Sources
    // (workspace ADR 0017), not a module, and a top-level entry is precisely the claim that it is
    // one: it would sit in the sidebar beside the features it evaluates, a level away from the data
    // sources that are its only subject. It is entered from the Data Sources page instead, by a
    // button carrying the same two gates the route does (Organization Admin + `cognition`).
    {
        id: 'teamChat',
        title: 'Team Chat',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:chat-bubble-left-right',
        link: '/teams-chat',
        permissionKey: 'group_chat'
    },
    {
        id: 'tasks',
        title: 'Tasks',
        type: 'basic',
        role: 'company',
        icon: 'heroicons_outline:check-circle',
        link: '/tasks',
        permissionKey: 'tasks'
    },

    // {
    //     id: 'htmlGenerator',
    //     title: 'HTML generator',
    //     type: 'basic',
    //     role: 'company',
    //     secondRole: 'manager',
    //     thirdRole: 'staff',
    //     icon: 'heroicons_outline:code-bracket',
    //     link: '/dashboards/html-generator',
    // },

    {
        id: 'chat',
        title: 'SAI Chat',
        type: 'basic',
        role: 'manager',
        secondRole: 'staff',
        icon: 'heroicons_outline:sparkles',
        link: '/ai-chat/agent-chat'

    },

    {
        id: 'customAgents',
        title: 'Custom Agents',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:rectangle-group',
        link: '/custom-agents',
        permissionKey: 'custom_agents'
    },
    {
        // The approvals inbox: everything across SAI waiting on this person's answer.
        //
        // Placed LAST among the tenant modules, after `customAgents` and before the platform admin
        // items, because it is not a feature area you go to in order to do work: it is where work
        // that has already started comes to find you. The admin entries below it are a different
        // audience (`role: 'admin'`) and never render alongside these.
        //
        // All three tenant roles, gated on `approvals` like every other feature item here. The
        // module shipped ungated because `/v1/approvals` carried no backend prefix rule; the backend
        // now mints `PermissionsEnum.approvals` and gates the prefix on it, granting the key to
        // every existing organization at rollout, so an organization that later loses the module
        // stops being offered a door that answers 403.
        //
        // The key here, `'approvals': 'approvals'` in `core/auth/guards/permission.guard.ts`, and
        // `'approvals'` in `MODULE_PERMISSION_KEYS` (`core/user/user.service.ts`) are one decision
        // written in three places. The last one is the non-obvious half: the sidebar's visibility
        // check is `this.permissions?.[item.permissionKey] === true`, a STRICT boolean compare, and
        // only keys listed in `MODULE_PERMISSION_KEYS` are normalised from the payload's
        // `'full_access'` / `'read_only'` level strings. An unlisted key leaves the item hidden from
        // exactly the people who hold it, with the route itself opening normally on a deep link.
        //
        // On a cold load the sidebar has no payload yet, so this item is hidden and then appears
        // when `getUserPermissions()` answers - the same fail-closed flicker every gated item here
        // has. See `core/user/module-permission-snapshot.ts` for why that direction is the safe one.
        //
        // The platform admin is excluded by the roles alone, matching the backend service, which
        // refuses a platform admin outright rather than answering with an empty queue.
        id: 'approvals',
        title: 'Approvals',
        type: 'basic',
        role: 'company',
        secondRole: 'manager',
        thirdRole: 'staff',
        icon: 'heroicons_outline:inbox',
        link: '/approvals',
        permissionKey: 'approvals',
    },
    {
        id: 'adminAgentSettings',
        title: 'Agents Configurations',
        type: 'collapsable',
        role: 'admin',
        icon: 'heroicons_outline:pencil-square',
        children: [
            {
                id: 'category',
                title: 'Category and Subcategory',
                type: 'basic',
                link: '/admin-agent-settings',
            },
            {
                id: 'api',
                title: 'Dynamic API',
                type: 'basic',
                link: '/admin-api',
            },
            {
                id: 'effortMapping',
                title: 'Effort Models',
                type: 'basic',
                link: '/admin-effort-mapping',
            },
            {
                id: 'customAgents',
                title: 'Custom Agents',
                type: 'basic',
                link: '/admin-custom-agents',
            },
            {
                id: 'systemInstructions',
                title: 'System Instructions',
                type: 'basic',
                link: '/system-instructions',
            },
            {
                id: 'ragChunkingSettings',
                title: 'RAG Chunking Settings',
                type: 'basic',
                link: '/admin-rag-settings',
            },
        ]
    },
    // {
    //     id: 'customAgents',
    //     title: 'Custom Agents',
    //     type: 'basic',
    //     role: 'admin',
    //     icon: 'heroicons_outline:ellipsis-horizontal-circle',
    //     link: '/admin-custom-agents',
    // },
    {
        id: 'adminUsers',
        title: 'Users',
        type: 'basic',
        role: 'admin',
        icon: 'heroicons_outline:clipboard-document-check',
        link: '/admin-users',
    },

    {
        // Terms, privacy, about and any other platform page. Shared by the website, the app and
        // the mobile app through the public /v1/content-pages API.
        id: 'adminContentPages',
        title: 'Content Pages',
        type: 'basic',
        role: 'admin',
        icon: 'heroicons_outline:document-text',
        link: '/admin-content-pages',
    },

    {
        id: 'adminContact',
        title: 'Support',
        type: 'basic',
        role: 'admin',
        icon: 'heroicons_outline:clipboard-document-check',
        link: '/admin-contact',
    },

];
